// Small presentational pieces shared by the Keys tab's Fobs list, Audit log and fob detail.
import React from "react";
import { Link } from "react-router-dom";
import { Check, Copy } from "lucide-react";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "components/ui/tooltip";
import { statusBadgeOutlineClass } from "shared/utils/statusChipColor";
import type { PhysicalKeyExternalSync } from "./siteKeysApi";
import {
  externalSyncSiteLabel,
  externalSyncStatusColor,
  worstExternalSync,
} from "./siteKeyLabels";

// A tooltip trigger that reads as plain text but can take keyboard focus (the tooltip opens on
// focus as well as hover).
const PLAIN_TEXT_TRIGGER =
  "cursor-default rounded-sm text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-300";

export function CopyButton({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = React.useState(false);

  React.useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(timer);
  }, [copied]);

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            aria-label={label}
            className="text-slate-500 hover:text-slate-900"
            onClick={async () => {
              // No Clipboard API (e.g. outside a secure context): don't claim to have copied.
              if (!navigator.clipboard) return;
              try {
                await navigator.clipboard.writeText(value);
                setCopied(true);
              } catch {
                // Clipboard access can be refused; the value is still visible to copy by hand.
              }
            }}
            size="icon-xs"
            type="button"
            variant="ghost"
          >
            {copied ? <Check /> : <Copy />}
          </Button>
        </TooltipTrigger>
        <TooltipContent>{copied ? "Copied" : label}</TooltipContent>
      </Tooltip>
      <span aria-live="polite" className="sr-only">
        {copied ? "Copied" : ""}
      </span>
    </>
  );
}

/** The 32-hex credential: monospace, optionally truncated with the full value as a tooltip, with copy. */
export function CredentialValue({
  credential,
  truncate = false,
}: {
  credential: string;
  truncate?: boolean;
}) {
  return (
    <span className="inline-flex max-w-full items-center gap-1">
      {truncate ? (
        <Tooltip>
          <TooltipTrigger asChild>
            {/* A plain-text button so keyboard users can focus it to read the full value; its
                accessible name is the whole credential (it's only visually truncated). */}
            <button
              className={`block max-w-36 truncate font-mono text-xs text-slate-600 ${PLAIN_TEXT_TRIGGER}`}
              type="button"
            >
              {credential}
            </button>
          </TooltipTrigger>
          <TooltipContent className="font-mono">{credential}</TooltipContent>
        </Tooltip>
      ) : (
        <span className="font-mono text-xs break-all text-slate-700">{credential}</span>
      )}
      <CopyButton label="Copy credential" value={credential} />
    </span>
  );
}

export function GuestLink({ guestId, name }: { guestId: string; name?: string | null }) {
  return (
    <Link to={`/admin/guests/${encodeURIComponent(guestId)}`}>{name || guestId}</Link>
  );
}

export function ExternalSyncStatusBadge({ sync }: { sync: PhysicalKeyExternalSync }) {
  return (
    <Badge
      className={statusBadgeOutlineClass(externalSyncStatusColor(sync.status))}
      variant="outline"
    >
      {sync.externalSystemType}: {sync.status}
    </Badge>
  );
}

/** "Brivo Tower (Brivo): Failed — reason". */
function externalSyncSummary(sync: PhysicalKeyExternalSync): string {
  const reason = sync.statusReason ? ` — ${sync.statusReason}` : "";
  return `${externalSyncSiteLabel(sync)} (${sync.externalSystemType}): ${sync.status}${reason}`;
}

/**
 * The Fobs list's External sync cell: the worst status across the fob's sister sites, labelled by
 * system, with every site, status and reason in the tooltip.
 */
export function ExternalSyncCell({
  syncs,
}: {
  syncs: readonly PhysicalKeyExternalSync[] | null | undefined;
}) {
  const worst = worstExternalSync(syncs);
  if (!syncs || !worst) {
    return <span className="text-slate-400">—</span>;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {/* Focusable so the per-site tooltip opens for keyboard users too; the label gives
            screen readers the same list. */}
        <button
          aria-label={`External sync: ${syncs.map(externalSyncSummary).join("; ")}`}
          className={`inline-flex items-center gap-1.5 ${PLAIN_TEXT_TRIGGER}`}
          type="button"
        >
          <ExternalSyncStatusBadge sync={worst} />
          {syncs.length > 1 && (
            <span className="text-xs text-slate-500">{syncs.length} sites</span>
          )}
        </button>
      </TooltipTrigger>
      <TooltipContent side="top">
        <ul className="space-y-1">
          {syncs.map((sync) => (
            <li key={sync.destinationSiteId}>{externalSyncSummary(sync)}</li>
          ))}
        </ul>
      </TooltipContent>
    </Tooltip>
  );
}
